<!-- callsteward-web/src/components/phoneSheet/openWindowPanel.vue -->
<template>
  <div class="ow-panel">
    <div class="ow-panel__header" @click="expanded = !expanded">
      <strong>Open Window</strong>
      <span v-if="activeWindow">⏱ {{ formatRemaining }}</span>
      <span class="ow-panel__toggle">{{ expanded ? '▼' : '▶' }}</span>
    </div>

    <div v-if="expanded" class="ow-panel__body">
      <!-- Setup mode: no active window -->
      <template v-if="!activeWindow">
        <div v-if="candidateInfo" class="ow-panel__info">
          {{ candidateInfo.total_candidates }} qualified employees
          · {{ candidateInfo.unassigned_calls }} open slots
        </div>
        <div class="ow-panel__controls">
          <label>Width:
            <select v-model="selectedWidth">
              <option v-for="w in widthPresets" :key="w" :value="w">{{ w }}</option>
            </select>
          </label>
          <label>Duration:
            <select v-model="selectedDuration">
              <option v-for="d in [5, 10, 15, 20, 30]" :key="d" :value="d">{{ d }}m</option>
            </select>
          </label>
          <button class="ow-panel__open-btn" @click="openWindow" :disabled="opening">
            {{ opening ? 'Opening...' : 'Open Window' }}
          </button>
        </div>
      </template>

      <!-- Active window: status panel -->
      <template v-else>
        <div class="ow-panel__status">
          ⏱ {{ formatRemaining }} · {{ tokensOpened }}/{{ activeWindow.width }} opened
          · {{ slotsFilled }} of {{ slotsTotal }} slots filled
        </div>
        <div class="ow-panel__employees">
          <div v-for="t in tokenStatuses" :key="t.employee_id" class="ow-panel__employee">
            <span v-if="t.actions_count > 0">✓</span>
            <span v-else-if="t.opened_at">👁</span>
            <span v-else>✉</span>
            {{ t.employee_name }}
            <span v-if="t.actions_count > 0"> — {{ t.actions_count }} action(s)</span>
          </div>
        </div>
        <button class="ow-panel__close-btn" @click="closeWindow">Close Window Early</button>
      </template>
    </div>
  </div>
</template>

<script>
import service from '../../services.js';
import api from '../../api';

export default {
  name: 'OpenWindowPanel',
  props: {
    jobId: { type: String, required: true },
    userToken: { type: String, required: true },
    clientId: { type: String, required: true },
  },
  data() {
    return {
      expanded: false,
      candidateInfo: null,
      activeWindow: null,
      tokenStatuses: [],
      slotsFilled: 0,
      slotsTotal: 0,
      selectedWidth: 10,
      selectedDuration: 15,
      widthPresets: [5, 10, 15, 20],
      opening: false,
      remainingSeconds: 0,
      pollTimer: null,
      countdownTimer: null,
    };
  },
  computed: {
    tokensOpened() {
      return this.tokenStatuses.filter(t => t.opened_at).length;
    },
    formatRemaining() {
      const m = Math.floor(this.remainingSeconds / 60);
      const s = this.remainingSeconds % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
  },
  async mounted() {
    await this.loadCandidates();
    await this.checkActiveWindow();
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
    clearInterval(this.countdownTimer);
  },
  methods: {
    v3(path) { return `/api/v3/${path}`; },
    async loadCandidates() {
      try {
        const res = await api.get(this.v3(`open-window/candidates/${this.jobId}`), {
          headers: { 'cs3-context-clientId': this.clientId },
          baseURL: '',
        });
        this.candidateInfo = res.data;
        this.widthPresets = res.data.width_presets;
        if (this.widthPresets.length > 0) {
          this.selectedWidth = this.widthPresets[0];
        }
      } catch (err) {
        console.error('Failed to load candidates', err);
      }
    },

    async checkActiveWindow() {
      try {
        const res = await api.get(this.v3(`open-window/job/${this.jobId}`), {
          headers: { 'cs3-context-clientId': this.clientId },
          baseURL: '',
        });
        const openWindow = res.data.windows.find(w => w.status === 'open');
        if (openWindow) {
          this.activeWindow = openWindow;
          this.expanded = true;
          this.startPolling(openWindow.id);
        }
      } catch (err) {
        console.error('Failed to check active window', err);
      }
    },

    async openWindow() {
      this.opening = true;
      try {
        const res = await api.post(this.v3('open-window'), {
          job_id: this.jobId,
          width: this.selectedWidth,
          duration_minutes: this.selectedDuration,
        }, {
          headers: { 'cs3-context-clientId': this.clientId },
          baseURL: '',
        });
        this.activeWindow = res.data.window;
        this.startPolling(this.activeWindow.id);
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.error;
        alert(msg || 'Failed to open window');
      } finally {
        this.opening = false;
      }
    },

    startPolling(windowId) {
      this.pollWindowStatus(windowId);
      this.pollTimer = setInterval(() => this.pollWindowStatus(windowId), 5000);
      this.remainingSeconds = Math.max(0, Math.floor(
        (new Date(this.activeWindow.expires_at) - new Date()) / 1000
      ));
      this.countdownTimer = setInterval(() => {
        if (this.remainingSeconds > 0) {
          this.remainingSeconds--;
        } else {
          this.activeWindow = null;
          clearInterval(this.pollTimer);
          clearInterval(this.countdownTimer);
          this.loadCandidates();
        }
      }, 1000);
    },

    async pollWindowStatus(windowId) {
      try {
        const res = await api.get(this.v3(`open-window/${windowId}`), {
          headers: { 'cs3-context-clientId': this.clientId },
          baseURL: '',
        });
        const w = res.data.window;
        this.tokenStatuses = res.data.tokens;
        this.slotsFilled = res.data.slots_filled;
        this.slotsTotal = res.data.slots_total;

        if (w.status !== 'open') {
          this.activeWindow = null;
          clearInterval(this.pollTimer);
          clearInterval(this.countdownTimer);
          this.loadCandidates();
        }
      } catch (err) {
        console.error('Poll failed', err);
      }
    },

    async closeWindow() {
      if (!confirm('Close this window early? Employees will no longer be able to confirm shifts.')) return;
      try {
        await api.post(this.v3(`open-window/${this.activeWindow.id}/close`), {}, {
          headers: { 'cs3-context-clientId': this.clientId },
          baseURL: '',
        });
        this.activeWindow = null;
        clearInterval(this.pollTimer);
        clearInterval(this.countdownTimer);
        this.loadCandidates();
      } catch (err) {
        alert('Failed to close window');
      }
    },
  },
};
</script>

<style scoped>
.ow-panel {
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-top: 12px;
  background: #fafafa;
}
.ow-panel__header {
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0f0f0;
  border-radius: 6px 6px 0 0;
}
.ow-panel__body { padding: 12px 14px; }
.ow-panel__info { color: #666; margin-bottom: 8px; font-size: 13px; }
.ow-panel__controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.ow-panel__controls label { font-size: 13px; }
.ow-panel__controls select { padding: 4px 8px; }
.ow-panel__open-btn {
  background: #007bff;
  color: #fff;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.ow-panel__open-btn:disabled { opacity: 0.5; }
.ow-panel__status { font-size: 14px; margin-bottom: 8px; }
.ow-panel__employees { font-size: 13px; max-height: 200px; overflow-y: auto; }
.ow-panel__employee { padding: 2px 0; }
.ow-panel__close-btn {
  margin-top: 10px;
  background: #dc3545;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
</style>
